import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import range from 'lodash/range'
import { type ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import ShiftHeader from 'domains/shifts/components/ShiftHeader'
import ShiftRowContainer from 'domains/shifts/components/ShiftRowContainer'
import { useShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'
import { intervalDifference } from 'domains/shifts/components/utils/intervalUtils'

const Container = styled.div({
  display: 'flex',
  height: '64px',
  zIndex: 0,
})

interface Props {
  warehouseLocationId: string
  activeShiftType: ShiftType
  shoppers: ShopperWithShifts[]
  zoneOffset: number
  devicePrioritizationEnabled: boolean
  onAddShift(shopperId: number, start: Date, end: Date, onCall: boolean, modalStyles?: Object): void
  onChangeShift(
    shopperId: number,
    shiftId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ): Promise<void>
  onDeleteShopper(shopperId: number): void
  recurShiftsToBeNonRecur: number[]
  ultrafastCrossDayDeliveryEnabled?: boolean
}

const ShopperShifts: FunctionComponent<Props> = ({
  warehouseLocationId,
  shoppers,
  onAddShift,
  onChangeShift,
  onDeleteShopper,
  zoneOffset,
  ...props
}) => {
  const {
    storeHours: { regularStoreOpen, regularStoreClose },
  } = useShiftManagementContext()
  const numberColumns = intervalDifference(regularStoreOpen, regularStoreClose)
  const refObject: { [key: string]: React.RefObject<HTMLDivElement>[] } = {}

  shoppers.map(shopper => {
    const rowRefs: React.RefObject<HTMLDivElement>[] = []
    range(numberColumns).forEach(i => (rowRefs[i] = React.createRef()))

    refObject[shopper.id] = rowRefs
  })

  return (
    <div data-testid="shopper-shifts-shift-container">
      {shoppers.map((shopper, i) => (
        <Container key={shopper.id}>
          {/* TODO: implement on delete function to update state run queries etc,
                since we will most likely be refactoring how the state is handled punting on it for now */}
          <ShiftHeader even={i % 2 === 0} onDelete={() => onDeleteShopper(shopper.id)}>
            {`${shopper.first_name} ${shopper.last_name}`}
          </ShiftHeader>
          <ShiftRowContainer
            warehouseLocationId={warehouseLocationId}
            first={i === 0}
            even={i % 2 === 0}
            zoneOffset={zoneOffset}
            onChangeShift={(...args) => onChangeShift(shopper.id, ...args)}
            onAddShift={(...args) => onAddShift(shopper.id, ...args)}
            shifts={shopper.shifts}
            shopperId={shopper.id}
            rowRefs={refObject[shopper.id]}
            {...props}
          />
        </Container>
      ))}
    </div>
  )
}

export default ShopperShifts
